import { FC, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { GLOBAL_ALERT_Z_INDEX } from "../../utils/alertManager";
import "./AlertMessage.scss";

export type ToastType = "success" | "error" | "info" | "warning";
export type AlertType = ToastType;

export interface AlertConfig {
  type: AlertType;
  message: string;
  closable?: boolean;
  onClose?: () => void;
}

export interface AlertMessageProps {
  config: AlertConfig | null;
  onClose: () => void;
  zIndex?: number;
}

export interface AppToastProps extends AlertMessageProps {}

const AUTO_HIDE_MS: Record<ToastType, number> = {
  success: 2200,
  info: 2200,
  warning: 2600,
  error: 4200,
};
const EXIT_MS = 620;

export const AppToast: FC<AppToastProps> = ({ config, onClose }) => {
  const [toast, setToast] = useState<(AlertConfig & { leaving: boolean }) | null>(null);

  useEffect(() => {
    if (!config) {
      setToast((current) => current ? { ...current, leaving: true } : null);
      return;
    }
    setToast({ ...config, leaving: false });
    const hideTimer = window.setTimeout(() => {
      setToast((current) => current ? { ...current, leaving: true } : null);
    }, AUTO_HIDE_MS[config.type]);
    return () => window.clearTimeout(hideTimer);
  }, [config?.message, config?.type, config?.closable]);

  useEffect(() => {
    if (!toast?.leaving) return;
    const removeTimer = window.setTimeout(() => {
      toast.onClose?.();
      setToast(null);
      onClose();
    }, EXIT_MS);
    return () => window.clearTimeout(removeTimer);
  }, [toast, onClose]);

  if (!toast) return null;
  const content = (
    <div
      className={`app-toast app-toast--${toast.type}${toast.leaving ? " app-toast--leaving" : ""}`}
      style={{ zIndex: GLOBAL_ALERT_Z_INDEX }}
      role="status"
      aria-live="polite"
    >
      <span className="app-toast__dot" />
      <span className="app-toast__message">{toast.message}</span>
      {toast.closable === true && (
        <button
          className="app-toast__close"
          type="button"
          aria-label="Close"
          onClick={() => setToast((current) => current ? { ...current, leaving: true } : null)}
        >
          &times;
        </button>
      )}
    </div>
  );
  return typeof document !== "undefined" ? createPortal(content, document.body) : content;
};

export const AlertMessage: FC<AlertMessageProps> = (props) => <AppToast {...props} />;
